import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import usePageMeta from "../hooks/usePageMeta";
import { BASE_URL } from "../config";
import { appendKioskShiftStatusParams } from "./manageContext";
import {
  resolveShiftSettingsClientId,
  loadClientShiftRoundingSettings,
  roundDateByShiftSettings,
} from "./shiftRounding";

const API_URL = BASE_URL;

export default function VerifyPhone() {
  usePageMeta("Verify Phone");
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [accountInfo, setAccountInfo] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resolvedShiftId, setResolvedShiftId] = useState(null);
  const [willCheckOut, setWillCheckOut] = useState(false);
  const [resolvedShiftDetails, setResolvedShiftDetails] = useState(null);
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds
  const [timerActive, setTimerActive] = useState(false);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Countdown timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setTimerActive(false);
      setError('OTP has expired. Please request a new one.');
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  // Resolve which shift this account should check into, the same way the
  // face-scan kiosk flow does via /api/face/check-shift-status, using the
  // clientId/location/date already present in the kiosk URL. Non-fatal if
  // it fails -- verification can still succeed without an auto check-in.
  const resolveShiftId = async (accountId) => {
    try {
      const qs = new URLSearchParams({ accountId: String(accountId) });
      appendKioskShiftStatusParams(qs, window.location.search);
      const response = await fetch(
        `${API_URL}/api/face/check-shift-status?${qs.toString()}`,
      );
      const data = await response.json();
      if (data?.hasShift && data?.shiftDetails?.shiftId) {
        setResolvedShiftId(data.shiftDetails.shiftId);
        setResolvedShiftDetails(data.shiftDetails);
        // Same rule the working face-scan flow uses (ShiftDetails.js):
        // already checked in and not yet checked out means this action
        // should be a check-out, not another check-in.
        setWillCheckOut(Boolean(data?.isCheckedIn && !data?.isCheckedOut));
      }
    } catch (err) {
      console.error("Resolve shift for check-in error:", err);
    }
  };

  const handleCheckPhone = async () => {
    setError("");
    setLoading(true);
    
    if (!phoneNumber) {
      setError("Please enter a phone number");
      setLoading(false);
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number");
      setLoading(false);
      return;
    }

    try {
      // First check if account exists with this phone number
      const response = await fetch(`${API_URL}/auth/check-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const data = await response.json();
      
      if (!data.success || !data.accountExists) {
        setError("No account found with this phone number. Please contact support.");
        setLoading(false);
        return;
      }

      setAccountInfo(data);
      setSuccess(`Account found for ${data.name}. Sending verification code...`);

      // Resolve the shift to check into once verification succeeds, and
      // send OTP -- run in parallel, neither should block the other.
      await Promise.all([resolveShiftId(data.accountId), handleSendOTP()]);

    } catch (err) {
      console.error("Phone check error:", err);
      setError("Unable to verify phone number. Please try again.");
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const data = await response.json();
      
      if (data.success) {
        setStep("otp");
        setMaskedEmail(data.maskedEmail || "");
        setSuccess("Verification code sent to your email!");
        setError("");
        setTimeLeft(180); // Reset to 3 minutes
        setTimerActive(true);

        // Show test OTP in development
        if (data.testOtp) {
          console.log("TEST OTP:", data.testOtp);
          setSuccess(prev => prev + ` (TEST: ${data.testOtp})`);
        }
      } else {
        setError(data.error || "Failed to send verification code");
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      setError("Unable to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Apply the same client-configured check-in/check-out rounding rules the
  // working face-scan flow applies (ShiftDetails.js), so the recorded time
  // isn't just the raw moment of verification.
  const resolveRoundedTimestamp = async () => {
    if (!resolvedShiftDetails) return null;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const roundingClientId = resolveShiftSettingsClientId({
        shiftDetails: resolvedShiftDetails,
        urlClientId: urlParams.get("clientId"),
      });
      const settings = await loadClientShiftRoundingSettings(roundingClientId);
      const roundingMode = willCheckOut
        ? settings?.checkOutMode
        : settings?.checkInMode;
      const actionDate = roundDateByShiftSettings(new Date(), roundingMode, settings, {
        field: willCheckOut ? "checkout" : "checkin",
        scheduledStart: resolvedShiftDetails.startDate,
        scheduledEnd: resolvedShiftDetails.endDate,
      });
      return actionDate.toISOString();
    } catch (err) {
      console.error("Resolve rounded timestamp error:", err);
      return null;
    }
  };

  const handleVerifyOTP = async () => {
    setError("");
    setLoading(true);

    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit verification code");
      setLoading(false);
      return;
    }

    try {
      const roundedTimestamp = resolvedShiftId
        ? await resolveRoundedTimestamp()
        : null;
      const response = await fetch(`${API_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber,
          otp,
          ...(resolvedShiftId
            ? {
                shiftId: resolvedShiftId,
                method: "otp",
                isCheckout: willCheckOut,
                ...(roundedTimestamp ? { timestamp: roundedTimestamp } : {}),
              }
            : {}),
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Stop the timer
        setTimerActive(false);

        // Phone verification successful - navigate to success. Identity
        // verification succeeding is not the same as a shift actually being
        // checked in/out -- checkedAction is null when no assigned shift was
        // found, so the success screen must say so rather than claim a
        // check-in/out happened.
        navigate("/checkin/success" + window.location.search, {
          state: {
            verificationMethod: "phone",
            account: data.account,
            isCheckout: data.checkedAction === "checkout",
            noShiftFound: !data.checkedAction,
          }
        });
      } else {
        setError(data.error || "Invalid verification code");
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      setError("Unable to verify code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/checkin/face-not-recognized" + window.location.search);
  };

  const handleBack = () => {
    setStep("phone");
    setOtp("");
    setError("");
    setSuccess("");
    setTimerActive(false);
    setTimeLeft(180);
  };

  const handleResendOTP = async () => {
    setError("");
    setLoading(true);
    setOtp("");

    try {
      const response = await fetch(`${API_URL}/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const data = await response.json();
      
      if (data.success) {
        setMaskedEmail(data.maskedEmail || maskedEmail);
        setTimeLeft(180); // Reset to 3 minutes
        setTimerActive(true);
        setSuccess("New verification code sent!");
        setError("");

        // Show test OTP in development
        if (data.testOtp) {
          console.log("TEST OTP:", data.testOtp);
          setSuccess(prev => prev + ` (TEST: ${data.testOtp})`);
        }
      } else {
        setError(data.error || "Failed to resend verification code");
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Unable to resend verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-4">
        {step === "phone" ? (
          <>
            <h1 className="text-lg font-bold text-center mb-4">
              Verify with your mobile phone number
            </h1>

            {/* Phone Input Form */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Mobile number on record with Talent*
              </label>
              
              <PhoneInput
                international
                defaultCountry="CA"
                value={phoneNumber}
                onChange={setPhoneNumber}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC1F00] focus:border-transparent text-sm"
                placeholder="Enter phone number"
                disabled={loading}
              />
            </div>

            {/* Account Info */}
            {accountInfo && (
              <div className="mb-4 bg-green-50 p-3 rounded-lg">
                <div className="text-sm text-green-800">
                  <div className="font-semibold">Account Found: {accountInfo.name}</div>
                  <div className="text-xs">Account ID: {accountInfo.accountId}</div>
                </div>
              </div>
            )}

            {/* Messages */}
            {error && (
              <p className="mb-4 text-xs text-red-600 text-center">{error}</p>
            )}
            {success && (
              <p className="mb-4 text-xs text-green-600 text-center">{success}</p>
            )}

            {/* Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleCheckPhone}
                disabled={loading}
                className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-base hover:bg-[#b91900] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "CHECKING..." : "SEND VERIFICATION CODE"}
              </button>

              <button
                onClick={handleCancel}
                disabled={loading}
                className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-base hover:bg-gray-400 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-lg font-bold text-center mb-4">
              Enter Verification Code
            </h1>

            <div className="mb-4 text-center">
              <p className="text-sm text-gray-600 mb-2">
                We sent a 6-digit code to your email:
              </p>
              <p className="font-semibold text-gray-800">{maskedEmail || "your email on file"}</p>
              <p className="text-xs text-gray-500 mt-1">
                Account: {accountInfo?.name}
              </p>
            </div>

            {/* OTP Input */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-2">
                6-Digit Verification Code
              </label>
              
              <input
                type="text"
                maxLength="6"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC1F00] focus:border-transparent text-center text-lg font-mono tracking-wider"
                placeholder="000000"
                disabled={loading}
              />
            </div>

            {/* Timer Display */}
            <div className="mb-4 text-center">
              {timerActive && timeLeft > 0 ? (
                <div className="text-sm text-gray-600">
                  Code expires in: <span className="font-mono text-[#DC1F00] font-semibold text-lg">{formatTime(timeLeft)}</span>
                </div>
              ) : timeLeft === 0 ? (
                <div className="text-sm text-red-600 font-semibold">
                  Code has expired
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Ready to verify
                </div>
              )}
            </div>

            {/* Messages */}
            {error && (
              <p className="mb-4 text-xs text-red-600 text-center">{error}</p>
            )}
            {success && (
              <p className="mb-4 text-xs text-green-600 text-center">{success}</p>
            )}

            {/* Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.length !== 6 || timeLeft === 0}
                className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-base hover:bg-[#b91900] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? "VERIFYING..." : "VERIFY CODE"}
              </button>

              <button
                onClick={handleBack}
                disabled={loading}
                className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-base hover:bg-gray-400 transition-colors"
              >
                BACK
              </button>

              <div className="text-center mt-4">
                <button
                  onClick={handleResendOTP}
                  disabled={loading || (timerActive && timeLeft > 0)}
                  className="text-sm text-[#DC1F00] hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Resend Code"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
